// src/pages/UmatList.js
import React, { useEffect, useMemo, useState } from "react";
import ContentLayout from "../layouts/ContentLayout";
import { useHistory, useLocation } from "react-router-dom";
import { toPinyinSync, capitalizePinyin } from '../utils/pinyinUtil';
import PERMISSIONS, { apiFetch, resolveUmatAvatarSync, getUmatDisplayLabel, getUmatDisplayLabelLine2, getUmatMetaLine } from '../utils/wkCommon';
import { getUser } from '../utils/auth';
import CardUmat from "../components/CardUmat";
import DetailUmat from "../components/DetailUmat";
import * as XLSX from "xlsx";
import {hasUmatPermissionAsync} from "../utils/permissionsAsync";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// helper untuk membuat absolute url untuk media Strapi
function absoluteUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return API_URL + url;
}

// normalize item dari Strapi (handle {id, attributes} atau flattened)
function normalizeItem(item) {
  if (!item) return null;
  if (item.id && item.attributes) {
    return { id: item.id, ...item.attributes };
  }
  return { ...item };
}

// map normalized Strapi item → UI/export row (shared by list load & excel export)
function toUmatRow(i) {
  return {
    id: i.id || i.umatID || Math.random(),
    umatID: i.umatID || i.code || "",
    chineseName: i.chineseName || i.chinese || "",
    pinyinName: i.pinyinName || i.pinyin || "",
    fullName: i.fullName || i.displayName || i.name || "",
    nickName: i.nickName || i.nickname || "",
    district: i.district || i.fotangWilayah || i.fotang || "",
    gender: i.gender || "",
    birthdate: i.birthdate || i.tanggalLahir || "",
    address: i.address || "",
    mobilePhone: i.mobilePhone || i.mobile || i.phone || "",
    email: i.email || "",
    LoginEmail: i.LoginEmail || "",
    raw: i,
  };
}

// minimal 3 karakter latin OR 1 han character
function isSearchable(q) {
  if (!q) return false;
  const s = q.trim();
  const hasHan = /\p{Script=Han}/u.test(s);
  if (hasHan) return true;
  const latinMatches = s.match(/[A-Za-z0-9]/g) || [];
  return latinMatches.length >= 3;
}

// helper: tanggal batas berdasarkan umur (YYYY-MM-DD)
// Start: 1 Jan pada tahun (todayYear - years)
// End: 31 Dec pada tahun (todayYear - years)
function dateYearsAgoStart(years) {
  const y = new Date().getFullYear() - Number(years);
  return `${y}-01-01`;
}

function dateYearsAgoEnd(years) {
  const y = new Date().getFullYear() - Number(years);
  return `${y}-12-31`;
}

export default function FilteredUmatList() {

  const authUser = getUser();

  const [canExportUmatData, setCanExportUmatData] = useState(false);
  const [canViewUmatAge, setCanViewUmatAge] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadPerms() {
      try {
        const email = authUser?.email;

        const a = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_LISTEXPORT);
        if (mounted) setCanExportUmatData(Boolean(a));

        const full = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_FULL);
        if (mounted) setCanViewUmatAge(!!full);
      } catch (e) {
        console.warn("loadPerms failed", e);
      } finally {
        //if (mounted) setPermsReady(true);
      }
    }
    loadPerms();
    return () => { mounted = false; };
  }, [authUser]);
  
  function calcAge(birthdate) {
    if (!birthdate) return "";
    const d = new Date(birthdate);
    if (isNaN(d)) return "";
    const diff = Date.now() - d.getTime();
    return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  }

  async function handleExportExcel() {
    if (exporting) return;
    setExporting(true);
    try {
      const PS = 100; // export page size per request
      // 1st page → discover total pages
      const first = await apiFetch(buildUmatsUrl({ keyword: searchValue, page: 1, pageSize: PS }));
      if (!first) {
        alert("Data tidak dapat dimuat (backend tidak merespons)");
        return;
      }
      const pageCount = first.meta?.pagination?.pageCount ?? 1;
      let allRaw = [...(first.data || [])];

      // remaining pages
      for (let p = 2; p <= pageCount; p++) {
        const j = await apiFetch(buildUmatsUrl({ keyword: searchValue, page: p, pageSize: PS }));
        if (j?.data) allRaw = allRaw.concat(j.data);
      }

      const allRows = allRaw.map(normalizeItem).map(toUmatRow);
      if (!allRows.length) {
        alert("Tidak ada data untuk diexport");
        return;
      }

      //console.log(`Exporting ${allRows.length} umat rows to Excel...`);
      //console.log("Sample row:", allRows[0]);
      const data = allRows.map(r => ({
        "Chinese Name": r.chineseName || "",
        "Pinyin": r.chineseName ? `${capitalizePinyin(toPinyinSync(r.chineseName))}` : "",
        "Full Name / Nickname": r.fullName || r.nickName || "",
        "Gender": r.gender || "",
        "Usia": calcAge(r.birthdate),
        "Area": r.raw.area ? r.raw.area.shortname : "",
        "District": r.district || "",
        "Mobile Phone": r.mobilePhone || "",
        "Jabatan Suci": r.raw.jabatanSuci || "",
        "SD 3 Hari": r.raw.sd3Hari || "",
        "Qingkou": r.raw.qingKou || "",
        "Tanzhu": r.raw.tanzhu || "",
        "Jiangyuan": r.raw.jiangYuan || "",
        "JiangShi": r.raw.jiang_Shi || "",
        "Dianchuanshi": r.raw.dianchuanshi || "",
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Umat");
      XLSX.writeFile(wb, `umat_export_${Date.now()}.xlsx`);
    } catch (err) {
      console.error("Export error:", err);
      alert("Export gagal: " + String(err));
    } finally {
      setExporting(false);
    }
  }

  const location = useLocation();
  const history = useHistory();

  const qs = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const filterId = qs.get("filter") || ""; // contoh: usiaIndah, ciqing, area, jiang_Shi
  const areaValue = qs.get("area") || "ALE"; // jika perlu custom area via qs

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Build URL with server-side filters based on filterId and optional keyword
  function buildUmatsUrl({ keyword = "", page = 1, pageSize = 50 } = {}) {
    const base = `${API_URL}/api/umats`;
    const params = new URLSearchParams();
    //params.append("populate", "*");
    params.append("fields[0]", "umatID");
    params.append("fields[1]", "chineseName");
    params.append("fields[2]", "pinyinName");
    params.append("fields[3]", "fullName");
    params.append("fields[4]", "nickName");
    params.append("fields[5]", "gender");
    params.append("fields[6]", "birthdate");
    params.append("fields[7]", "address");
    params.append("fields[8]", "mobilePhone");
    params.append("fields[9]", "email");
    params.append("fields[10]", "LoginEmail");
    params.append("fields[11]", "fatherName");
    params.append("fields[12]", "fatherPhoneNo");
    params.append("fields[13]", "motherName");
    params.append("fields[14]", "motherPhoneNo");
    params.append("fields[15]", "tsfyUmatID");

    // keperluan export
    params.append("fields[16]", "jabatanSuci");
    params.append("fields[17]", "sd3Hari");
    params.append("fields[18]", "qingKou");
    params.append("fields[20]", "tanzhu");
    params.append("fields[21]", "jiangYuan");
    params.append("fields[22]", "jiang_Shi");
    params.append("fields[23]", "dianchuanshi");

    // area (dipakai di detail)
    params.append("populate[0]", "area");
    params.append("populate[area][fields][0]", "name");
    params.append("populate[area][fields][1]", "code");
    params.append("populate[area][fields][2]", "shortname");

    // data orangtua
    params.append("populate[father_umat_item][fields][0]", "fullName");
    params.append("populate[father_umat_item][fields][1]", "nickName");
    params.append("populate[father_umat_item][fields][2]", "chineseName");
    params.append("populate[father_umat_item][fields][3]", "mobilePhone");

    params.append("populate[mother_umat_item][fields][0]", "fullName");
    params.append("populate[mother_umat_item][fields][1]", "nickName");
    params.append("populate[mother_umat_item][fields][2]", "chineseName");
    params.append("populate[mother_umat_item][fields][3]", "mobilePhone");

    // profilepic:
    params.append("populate[profilepic]", "true");

    params.append("pagination[page]", String(page));
    params.append("pagination[pageSize]", String(pageSize));
    params.append("filters[$and][0][$or][0][passedAway][$eq]", "false");
    params.append("filters[$and][0][$or][1][passedAway][$null]", "true");
    params.append("filters[$and][1][$or][0][dianchuanshi][$eq]", "false");
    params.append("filters[$and][1][$or][1][dianchuanshi][$null]", "true");

    // apply server-side filter mapping
    // we'll support several filterId values per request
    if (filterId === "changqing") {
        // either field usiaIndah = true OR age >= 45
        params.append("filters[$or][0][usiaIndah][$eq]", "true");
        params.append("filters[$or][1][birthdate][$lte]", dateYearsAgoStart(45));

    } else if (filterId === "ciqing") {
        // usia antara 16 - 35 OR field ciqing = true
        params.append("filters[$or][0][ciqing][$eq]", "true");
        params.append("filters[$or][1][$and][0][birthdate][$gte]", dateYearsAgoStart(35)); // lahir >= batas bawah (lebih muda dari 35)
        params.append("filters[$or][1][$and][1][birthdate][$lte]", dateYearsAgoEnd(16)); // lahir <= batas atas (lebih tua dari 16)

    } else if (filterId === "chengzhang") {
        // usia 12–15 OR field chengzhang = true
        params.append("filters[$or][0][smp_SMA][$eq]", "true");
        params.append("filters[$or][1][$and][0][birthdate][$gte]", dateYearsAgoStart(15));
        params.append("filters[$or][1][$and][1][birthdate][$lte]", dateYearsAgoEnd(13));

    } else if (filterId === "ertong") {
        // usia <= 11 OR parittaAnak = true
        params.append("filters[$or][0][erTongBan][$eq]", "true");
        params.append("filters[$or][1][$and][0][birthdate][$gte]", dateYearsAgoStart(12));
        params.append("filters[$or][1][$and][1][birthdate][$lte]", dateYearsAgoEnd(5));

    } else if (filterId === "ALE") {
        params.append("filters[area][code][$eq]", "ALE"); //Li En

    } else if (filterId === "ALC") {
        params.append("filters[area][code][$eq]", "ALC"); //Li Ci

    } else if (filterId === "AJH") {
        params.append("filters[area][code][$eq]", "AJH"); //JingHua

    } else if (filterId === "AZD") {
        params.append("filters[area][code][$eq]", "AZD"); //ZhanDao

    } else if (filterId === "AZX") {
        params.append("filters[area][code][$eq]", "AZX"); //ZhanXin

    } else if (filterId === "ADH") {
        params.append("filters[area][code][$eq]", "ADH"); //DeHua

    } else if (filterId === "AZC") {
        params.append("filters[area][code][$eq]", "AZC"); //ZhangCheng, Pringsewu

    } else if (filterId === "AZL") {
        params.append("filters[area][code][$eq]", "AZL"); // ZhanLian, PangkalPinang

    } else if (filterId === "AZZ") {
        params.append("filters[area][code][$eq]", "AZZ");   //ZhanZhong, SungaiLiat

    } else if (filterId === "AMS") {
        params.append("filters[area][code][$eq]", "AMS");          //MiaoShan, Solo

    } else if (filterId === "JiangShi") {
        params.append("filters[jiang_Shi][$eq]", "true");

    } else if (filterId === "JiangYuan") {
        params.append("filters[jiangYuan][$eq]", "true");

    } else if (filterId === "TanZhu") {
        params.append("filters[tanzhu][$eq]", "true");

    } else if (filterId === "QingKou") {
        params.append("filters[qingKou][$eq]", "true");

    } else if (filterId === "BanShi") {
        params.append("filters[sd3Hari][$eq]", "true");

    } else if (filterId === "NoArea") {
        params.append("filters[area][$null]", "true");

    } else if (filterId === "NoPhoto") {
        params.append("filters[profilepic][$null]", "true");

    } else {
        // fallback
        params.append("filters[active][$eq]", "true");
    }

    // keyword search same approach as DataUmat2: OR across fields with $containsi
    const q = (keyword || "").trim();
    if (q) {
      const fields = ["umatID", "pinyinName", "fullName", "nickName", "email"];
      if(hasHan(q))
      {
        fields = ["chineseName"]; //replace to find only chineseName
      }
      fields.forEach((field, idx) => {
        const key = `filters[$and][0][$or][${idx}][${field}][$containsi]`;
        // we add search inside an $and[0][$or] wrapper so it composes with existing filters
        params.append(key, q);
      });
    }

    return `${base}?${params.toString()}`;
  }

  function hasHan(q)
  {
    // check for at least one Han character (Chinese)
    const hasHan = /\p{Script=Han}/u.test(q);
    if (hasHan) return true;
    else return false;
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const url = buildUmatsUrl({ keyword: searchValue, page, pageSize });
        //console.debug("Fetching umats filtered:", url);
        //const jwt = getJwt();
        //const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};
        //const resp = await fetch(url, { headers }); // tanpa credentials
        //if (!resp.ok) {
        //  const text = await resp.text();
        //  throw new Error(`HTTP ${resp.status}: ${text}`);
        //}
        //const json = await resp.json();
        const json = await apiFetch(url);

        //const rawArr = Array.isArray(json) ? json : (json.data || []);
        if (!json) {
          console.warn("API returned null", url);
          setRows([]);
          setMeta(null);
          setError("Data tidak dapat dimuat");
          return;
        }

        const rawArr = Array.isArray(json)
          ? json
          : (json?.data || []);
          
        const normalized = rawArr.map(normalizeItem).map(toUmatRow);
        if (!mounted) return;
        setRows(normalized);
        setMeta(json.meta || null);
      } catch (err) {
        console.error("Fetch error:", err);
        if (mounted) setError(String(err));
        setRows([]);
        setMeta(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [filterId, areaValue, searchValue, page, pageSize]);

  // UI rendering list
  const listNode = useMemo(() => {
    if (loading) return <div className="empty">Loading...</div>;
    if (error) return <div className="empty" style={{ color: "crimson" }}>Error: {error}</div>;
    if (!rows || rows.length === 0) return <div className="empty">No results</div>;

    return rows.map(r => {
      const line1 = getUmatDisplayLabel(r);
      const line2 = getUmatDisplayLabelLine2(r);
      const line3 = getUmatMetaLine(r);
      const avatar = resolveUmatAvatarSync(r.raw);

      return (
        <CardUmat
          key={r.id}
          avatar={avatar}
          line1={line1}
          line2={line2}
          line3={line3}
          onClick={() => setSelected(r)}
        />
      );
    });
  }, [rows, loading, error]);

  const detailNode = selected ? <DetailUmat umat={selected} onClose={() => setSelected(null)} canViewUmatAge={canViewUmatAge} /> : null;
/*   const detailNode = selected ? (() => {
    const qrData = selected.umatID || selected.id || selected.email || "";
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`;
    const avatar = resolveUmatAvatarSync(selected.raw);

    return (
      <div className="detail-card">
        <div className="detail-head">
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <img src={avatar} alt={selected.fullName || selected.nickName} style={{ width: 72, height: 72, borderRadius: 8, objectFit: "cover" }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{selected.fullName || selected.nickName}</div>
              <div style={{ opacity: 0.8 }}>{selected.chineseName ? `${selected.chineseName}${' '}${capitalizePinyin(toPinyinSync(selected.chineseName))}` : ""}</div>
            </div>
          </div>
          <button className="btn-icon close-detail" onClick={() => setSelected(null)}>✕</button>
        </div>

        <div className="detail-body">
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="detail-row"><strong>Gender:</strong> {selected.gender || "-"}</div>
              <div className="detail-row" style={{ display: "none" }}>
                <strong>Usia:</strong>{" "}
                {selected.birthdate
                  ? `${Math.floor((new Date() - new Date(selected.birthdate)) / (365.25 * 24 * 60 * 60 * 1000))} tahun`
                  : "-"}
              </div>
              <div className="detail-row"><strong>Area:</strong>  { (selected.area ? selected.area.name : "") || "-" }</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="detail-row"><strong>Mobile:</strong> {selected.mobilePhone || "-"}</div>
              <div className="detail-row"><strong>Email:</strong> {selected.email || "-"}</div>
            </div>
          </div>

          <div style={{ marginTop: 12, textAlign: "center" }}>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>QR dari Kode Umat</div>
            <img src={qrUrl} alt={`QR-${selected.umatID}`} style={{ width: 160, height: 160, display: "inline-block" }} />
            <div style={{ fontSize: 16, marginTop: 6 }}><strong>{selected.umatID}</strong></div>
          </div>
        </div>
      </div>
    );
  })() : null; */

  function handleSearchChange(v) {
    setSearchValue(v);
    setPage(1);
    setSelected(null);
    // do not clear rows if search is empty: we want initial filtered list to show even if no search
  }

  function handlePrevPage() {
    if (!meta) return;
    const cur = meta.pagination?.page || page;
    if (cur > 1) setPage(cur - 1);
  }
  function handleNextPage() {
    if (!meta) return;
    const cur = meta.pagination?.page || page;
    const pageCount = meta.pagination?.pageCount || null;
    if (!pageCount || cur < pageCount) setPage(cur + 1);
  }

  const paginationNode = (() => {
    const current = meta?.pagination?.page || page || 1;
    const pageCount = meta?.pagination?.pageCount ?? null;
    const total = meta?.pagination?.total ?? null;

    if (pageCount !== null && pageCount <= 1) return null;
    if (pageCount === null && rows && rows.length <= pageSize) return null;

    const middle = pageCount ? `Halaman ${current} dari ${pageCount}` : `Halaman ${current}${total ? ` • ${total} total` : ""}`;

    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, padding: "8px 0" }}>
        <button className="btn-secondary" onClick={handlePrevPage} disabled={current <= 1}>{"< Prev"}</button>
        <div style={{ padding: "4px 12px", borderRadius: 6, background: "transparent", minWidth: 180, textAlign: "center", fontWeight: 600 }}>
          {middle}
        </div>
        <button className="btn-secondary" onClick={handleNextPage} disabled={pageCount ? current >= pageCount : false}>{"Next >"}</button>
      </div>
    );
  })();

  const infoNode = (
    <div className="tip-text">
      {meta ? `Menampilkan halaman ${meta.pagination.page} / ${meta.pagination.pageCount} — ${meta.pagination.total} total` : `${rows.length} data ditemukan`}
    </div>
  );

  const filterNode = (
    <div>
      <div className="filter-head"><strong>Filters</strong></div>
      <div className="filter-controls">
        <div style={{ marginBottom: 8 }}>Filter aktif: <strong>{filterId || "(tidak ada)"}</strong></div>
        <label>
          Cari:
          <input value={searchValue} onChange={(e) => handleSearchChange(e.target.value)} placeholder="kode / pinyin / nama / nickname / email" />
        </label>
        <div style={{ marginTop: 12 }}>
          <button className="btn-secondary" onClick={() => { handleSearchChange(""); history.push(location.pathname + `?filter=${filterId}&area=${areaValue}`); }}>Reset</button>
        </div>
      </div>
    </div>
  );

  const headerActions = (
    <>
      <button className="btn-primary" onClick={handleExportExcel} disabled={exporting}>
        {exporting ? "Exporting…" : "Export Excel"}
      </button>
    </>
  );

  const mainWithPaging = (
    <>
      {paginationNode}
      <div>
        {listNode}
      </div>
      {paginationNode}
    </>
  );

  return (
    <ContentLayout
      title="DAFTAR UMAT - Filtered"
      searchValue={searchValue}
      onSearchChange={(v) => handleSearchChange(v)}
      filterNode={filterNode}
      infoNode={infoNode}
      mainNode={mainWithPaging}
      detailNode={detailNode}
      headerActions={headerActions}
      onCloseDetail={() => setSelected(null)}
    />
  );
}
